import { Severity, severityLabelMap, SEVERITY_ORDER, } from '../contracts/shared/severity';
/**
 * Score mapping based on severity level. Matches the backend's risk-scoring
 * logic so extension-side scoring stays in sync with how scans are persisted.
 */
export const SEVERITY_SCORE = {
    [Severity.Low]: 20,
    [Severity.Medium]: 50,
    [Severity.High]: 80,
    [Severity.Critical]: 100,
};
/**
 * Derive an overall risk level from a list of issues by picking the highest
 * severity present. Returns `'low'` for an empty issue list (no risk).
 *
 * Use when consumers need a single rolled-up severity to display alongside a
 * heterogenous issue set (e.g. extension scan results in the IDE status bar).
 */
export function riskLevelFromIssues(issues) {
    if (issues.length === 0)
        return Severity.Low;
    return issues.reduce((max, issue) => SEVERITY_ORDER[issue.severity] > SEVERITY_ORDER[max]
        ? issue.severity
        : max, Severity.Low);
}
export function normalizeSeverityValue(severity) {
    if (!severity) {
        return null;
    }
    const normalized = severity.trim().toLowerCase();
    if (!normalized) {
        return null;
    }
    if (!Object.prototype.hasOwnProperty.call(SEVERITY_ORDER, normalized)) {
        return null;
    }
    return normalized;
}
function getSeverityOrderValue(severity) {
    const normalized = normalizeSeverityValue(severity);
    if (!normalized) {
        return null;
    }
    return SEVERITY_ORDER[normalized];
}
/**
 * Check if actual severity meets or exceeds minimum threshold.
 *
 * FAIL-CLOSED BEHAVIOR:
 * - Unknown/invalid severities return false (no notification)
 * - Input is normalized (trim, lowercase) but NOT defaulted to 'medium'
 * - Use parseSeverity() BEFORE calling this if you want lenient defaults
 *
 * WHY FAIL-CLOSED (not normalize to 'medium')?
 * - Safer for notifications: don't spam users with invalid/unexpected data
 * - Unified behavior: Slack and alert notifications use the same function
 * - Explicit normalization: caller uses parseSeverity() first if lenient behavior needed
 *
 * @example
 * meetsMinSeverity('HIGH', 'medium')    // true (high >= medium)
 * meetsMinSeverity('low', 'high')       // false (low < high)
 * meetsMinSeverity('invalid', 'medium') // false (unknown → fail-closed)
 * meetsMinSeverity(null, 'medium')      // false (null → fail-closed)
 */
export function meetsMinSeverity(actualSeverity, minSeverity) {
    const actualOrder = getSeverityOrderValue(actualSeverity);
    const minOrder = getSeverityOrderValue(minSeverity);
    if (actualOrder === null || minOrder === null) {
        return false;
    }
    return actualOrder >= minOrder;
}
const SEVERITY_ESCALATION = {
    [Severity.Low]: Severity.Medium,
    [Severity.Medium]: Severity.High,
    [Severity.High]: Severity.Critical,
    [Severity.Critical]: Severity.Critical,
};
/**
 * One severity level up, capped at Critical (KIR-3612 Action A: findings
 * raised while a coding agent runs autonomously). An unknown value is returned
 * unchanged, so a malformed severity is never silently promoted.
 */
export function escalateSeverity(severity) {
    const normalized = normalizeSeverityValue(severity);
    return normalized == null ? severity : SEVERITY_ESCALATION[normalized];
}
/*
  getSeverityLabel returns the display label for a given Severity.
  Returns 'Unknown' if the risk level is not in the map.
  Whitespace is trimmed before processing.
*/
export function getSeverityLabel(severity) {
    if (!severity) {
        return 'Unknown';
    }
    const trimmed = String(severity).trim();
    if (!trimmed) {
        return 'Unknown';
    }
    const normalized = trimmed.toLowerCase();
    if (normalized === 'info') {
        return 'Info';
    }
    if (normalized in severityLabelMap) {
        return severityLabelMap[normalized];
    }
    return 'Unknown';
}
/*
  sortBySeverity sorts an array of items by their severity level.
  Items are sorted in descending order: critical → high → medium → low.
  Items without a severity or with unknown severity values are placed at the end.
  The original array is not mutated; a new sorted array is returned.
*/
export function sortBySeverity(items) {
    return [...items].sort((a, b) => {
        const aOrder = getSeverityOrderValue(a.severity) ?? 0;
        const bOrder = getSeverityOrderValue(b.severity) ?? 0;
        return bOrder - aOrder;
    });
}
/**
 * Convert severity string to Severity enum
 * @param severity - Severity as string (e.g., "critical", "high", "medium", "low") - case insensitive
 * @returns Severity enum value, defaults to Severity.Medium if invalid
 */
export function parseSeverity(severity) {
    return normalizeSeverityValue(severity) ?? Severity.Medium;
}
//# sourceMappingURL=severity.js.map